<template>
  <TransitionRoot as="template" :show="isPresentingModal()">
    <Dialog class="relative z-10" @close="dismissModal">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
              <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button type="button" class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-jananavy-500 focus:ring-offset-2" @click="dismissModal">
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <DialogTitle as="h3" class="text-base font-semibold text-gray-900">{{ isSuperAdmin() ? 'Switch Organisation' : 'Account' }}</DialogTitle>
              <div class="mt-4" v-if='isSuperAdmin()'>
                <SearchInput v-model='searchConnector.model' class='w-full mb-4' v-if='isSuperAdmin()' :isLoading='query.isLoading()' />
                <ul class='divide-y' v-if='query.hasItems() && !query.isLoading()'>
                  <li v-for='item in query.getItems()'>
                    <RouterLink class='flex justify-between gap-2 items-center w-full py-1' :to='`/?organisationId=${item.id}`' @click='chooseOrganisation($event)'>
                      <span>{{ organisationCollection.getName(item) }}</span>
                      <ArrowRightIcon class='w-4 h-4' />
                    </RouterLink>
                  </li>
                </ul>
                <template v-if='!query.isLoading()'>
                  <ErrorNotice v-if='query.hasError()' :error='query.getError()' class='mt-4' />
                  <WarningNotice v-if='!query.isEmpty() && !query.hasItems()' class='mt-4'>
                    <template #title>No {{ organisationCollection.getPluralLabel(true) }} found.</template>
                  </WarningNotice>
                </template>
              </div>
              <div class="mt-4 p-4 bg-gray-100 rounded-lg" v-if='!isSuperAdmin()'>
                <strong>{{ getOrganisationName() }}</strong><br />
                {{ getEmailAddress() }}
              </div>
              <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <a :href='config.auth0LogoutUrl' class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-2 sm:mt-0">Logout</a>
                <button type="button" class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0" @click="dismissModal" v-if='!isSuperAdmin() || shouldShowSuperAdmin()'>Cancel</button>
                <RouterLink class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0" @click="goToSuperAdmin($event)" v-if='isSuperAdmin() && shouldShowOrganisation()' to='/'>Super Admin</RouterLink>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>

import { ref, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { CheckIcon, XMarkIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';

import { dismissModal, isPresentingModal, isSuperAdmin, shouldShowSuperAdmin, shouldShowOrganisation, getEmailAddress, getOrganisationName, isAuthenticated } from '@/kernel/session';

import { config } from '@/kernel/config';
import { usePayloadQuery } from '@/kernel/api';
import { getCollection } from '@/kernel/collections';

import SearchInput from '@/components/inputs/search-input.vue';

import ErrorNotice from '@/components/notices/error-notice.vue';
import WarningNotice from '@/components/notices/warning-notice.vue';

const organisationCollection = getCollection('organisation');

const query = usePayloadQuery(organisationCollection.getApiIndexEndpoint(), {
  sort: organisationCollection.getDefaultSort()
});

const goToSuperAdmin = (event) => {
  if (event.metaKey || event.ctrlKey) {
    return;
  }

  dismissModal();
}

const chooseOrganisation = (event, organisation) => {
  if (event.metaKey || event.ctrlKey) {
    return;
  }

  dismissModal();
}

const searchConnector = query.connectSearch(organisationCollection.getSearchKeys());

if (isAuthenticated()) {
  query.doQuery();
}

watch(() => isAuthenticated(), () => {
  query.doQuery();
});

</script>
